import { HttpErrorResponse } from '@angular/common/http';

import { AuthFailureCause } from '@ninety/mobile/platform/auth';

/**
 * An error a handler has already classified.
 *
 * The sibling of `VaultError`, for the two servers rather than for the Vault, and named the same
 * way: the field is `failure` rather than `cause` because `Error.cause` is the standard slot for a
 * wrapped error, and this is a classification instead.
 *
 * Adapters raise this when they know something the caller cannot see — that an empty Amplify
 * session means the Durable Root bought nothing, or that a response carrying half a session is not
 * a session. Everything else they let through as-is for {@link classifyFailure} to read.
 */
export class AuthFailureError extends Error {
  constructor(
    readonly failure: AuthFailureCause,
    message?: string
  ) {
    super(message ?? failure.kind);
    this.name = 'AuthFailureError';
  }
}

/**
 * Decide what an error raised while restoring a session means.
 *
 * This is the boundary where an exception becomes a cause the machine can route on, and it is the
 * one place in the auth path allowed to look at error shapes. Matching on `err?.name` at the call
 * site instead is what puts a transient network failure and a revoked token on the same path.
 *
 * **An error it does not recognise is rethrown, not classified.** That is a bug rather than a
 * domain outcome, and the seam reports it as `auth.command_failed` and leaves the state alone — a
 * cause guessed at here would end a session the error said nothing about. A bug on the launch path
 * leaves someone on the splash screen, which is why that signal is alertable rather than folded into
 * an error log.
 */
export function classifyFailure(error: unknown): AuthFailureCause {
  if (error instanceof AuthFailureError) {
    return error.failure;
  }
  if (error instanceof HttpErrorResponse) {
    return httpCause(error.status);
  }
  throw error;
}

/**
 * A status becomes a cause.
 *
 * Only 401 and 403 are a rejection, because `resourceServerRejected` is the one cause here that
 * destroys the Durable Root and only those two mean the Resource Server refused *us*. A 400 or a
 * 404 from a misdeployed endpoint is a fault, not a refusal, and must not sign out every person on
 * the platform. Everything else — 0 for an unreachable server, a timeout, a 429, a 5xx — is the
 * server not answering, so it retries.
 */
function httpCause(status: number): AuthFailureCause {
  return status === 401 || status === 403 ? { kind: 'resourceServerRejected', status } : { kind: 'network' };
}
